import React, { useState, useRef, useCallback } from 'react';

interface AskNeuButtonProps {
  onNeuResponse: (question: string, response: string) => void;
  disabled?: boolean;
}

const AskNeuButton: React.FC<AskNeuButtonProps> = ({ onNeuResponse, disabled = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });
      streamRef.current = stream;

      mediaRecorder.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
        processVoiceQuery(audioBlob);
      };

      audioChunks.current = [];
      mediaRecorder.current.start(1000);
      setIsRecording(true);
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError('Microphone access denied');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      setIsRecording(false);
      
      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
        });
      }
    }
  }, [isRecording]);

  const processVoiceQuery = useCallback(async (audioBlob: Blob) => {
    try {
      setIsTranscribing(true);
      setError(null);

      // Step 1: Transcribe the voice query
      const formData = new FormData();
      formData.append('file', audioBlob, 'ask-neu.webm');

      const transcribeResponse = await fetch('/api/voice-to-text', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!transcribeResponse.ok) {
        throw new Error('Failed to transcribe voice query');
      }

      const transcribeResult = await transcribeResponse.json() as { text?: string; transcript?: string };
      const question = transcribeResult.text || transcribeResult.transcript || '';

      if (!question.trim()) {
        throw new Error('No speech detected. Please try again.');
      }

      setIsTranscribing(false);
      setIsSearching(true);

      // Step 2: Send the transcribed question to Neural Search
      const searchResponse = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          message: question,
          conversationHistory: [], // Fresh conversation for each Ask Neu query
          inputMethod: 'voice',
        }),
      });

      if (!searchResponse.ok) {
        throw new Error('Failed to get response from Neu');
      }

      const searchResult = await searchResponse.json() as { content: string };
      const response = searchResult.content || 'Sorry, I could not find relevant information.';

      // Pass both question and response to parent
      onNeuResponse(question, response);

    } catch (err) {
      console.error('Ask Neu error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process voice query');
    } finally {
      setIsTranscribing(false);
      setIsSearching(false);
    }
  }, [onNeuResponse]);

  const handleToggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // Auto-clear error after 3 seconds
  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const getButtonState = () => {
    if (isSearching) return 'searching';
    if (isTranscribing) return 'transcribing';
    if (isRecording) return 'recording';
    return 'ready';
  };

  const buttonState = getButtonState();
  const isProcessing = isTranscribing || isSearching;

  return (
    <div className="relative">
      <button
        onClick={handleToggleRecording}
        disabled={disabled || isProcessing}
        className={`p-2 rounded hover:bg-gray-100 transition-all duration-200 ${
          buttonState === 'recording' ? 'bg-blue-100 text-blue-600' :
          buttonState === 'transcribing' ? 'bg-yellow-100 text-yellow-600' :
          buttonState === 'searching' ? 'bg-purple-100 text-purple-600' :
          'text-gray-600'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        title={
          buttonState === 'recording' ? 'Recording... Click to stop' :
          buttonState === 'transcribing' ? 'Transcribing speech...' :
          buttonState === 'searching' ? 'Neu is thinking...' :
          'Ask Neu (Voice)'
        }
      >
        {buttonState === 'recording' && (
          <div className="relative">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
            </svg>
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
          </div>
        )}
        
        {buttonState === 'transcribing' && (
          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-yellow-600"></div>
        )}
        
        {buttonState === 'searching' && (
          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-purple-600"></div>
        )}
        
        {buttonState === 'ready' && (
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
            </svg>
            <span className="text-xs font-medium">Neu</span>
          </div>
        )}
      </button>

      {/* Error tooltip */}
      {error && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-red-100 text-red-700 text-xs rounded-lg border border-red-200 whitespace-nowrap z-10">
          {error}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-200"></div>
        </div>
      )}
    </div>
  );
};

export default AskNeuButton; 